import type { FileState } from '@atlaskit/media-client';
import { globalMediaEventEmitter } from '@atlaskit/media-client';
import type { MediaProvider } from '@atlaskit/editor-common/provider-factory';
import type { MediaClientConfig, AuthProvider as MediaAuthProvider } from '@atlaskit/media-core';
import type { UploadRejectionData } from '@atlaskit/media-picker';

import { fg } from '@confluence/feature-gating';
import { PDF_EXPORT_PAGE } from '@confluence/named-routes/entry-points/routes';

import { cacheWithExp } from '../cacheWithExp';
// eslint-disable-next-line import/no-cycle
import { getTemplateMediaContextValues, getTemplateMediaAuth } from '../TemplateMediaProvider';

import type {
	MediaDownloadTokenQuery_content_nodes,
	MediaDownloadTokenQuery_mediaConfiguration,
} from './__types__/MediaDownloadTokenQuery';
import {
	fetchMediaToken,
	fetchExternalShareMediaToken,
	getUploadToken,
	getIsExportEnabledForContent,
	getCommentUploadToken,
} from './graphql-helpers';
import { attachUploadedMedia } from './media-helpers';

type ConfigOptions = {
	contentId: string;
	spaceKey?: string | null;
	mediaDspContentId?: string;
	isExternalShare: boolean;
	mediaToken?: string;
};

type EditorMediaProviderOptions = {
	contentType: string;
	cloudId?: string;
	onUploadRejection?: (data: UploadRejectionData) => boolean;
} & ConfigOptions;

export type TokenData = {
	config: ConfigData;
	token: string;
	collectionId?: string;
};

export type ConfigData = MediaDownloadTokenQuery_mediaConfiguration;

const DEFAULT_CONFIG: ConfigData = { clientId: '', fileStoreUrl: '' };

export type AuthProvider = {
	clientId: string;
	baseUrl: string;
	token: string;
};

const cachedGetDownloadData = cacheWithExp<TokenData>(
	async ({ contentId, isExternalShare }: Pick<ConfigOptions, 'contentId' | 'isExternalShare'>) => {
		const response = await (isExternalShare ? fetchExternalShareMediaToken : fetchMediaToken)(
			contentId,
		);
		const data = response?.data;
		const externalData = data?.['singleContent'];
		const content: MediaDownloadTokenQuery_content_nodes = isExternalShare
			? externalData
			: data?.['content']?.nodes?.[0];

		return {
			token: content?.body?.atlas_doc_format?.mediaToken?.token || '',
			config: data?.mediaConfiguration || DEFAULT_CONFIG,
		};
	},
	(json) => json.token,
);

type AuthProviderDataGetter = () => Promise<TokenData>;

export const createAuthProvider =
	(dataGetter: AuthProviderDataGetter): MediaAuthProvider =>
	async () => {
		const data = await dataGetter();
		return {
			token: data?.token || '',
			clientId: data?.config?.clientId || '',
			baseUrl: data?.config?.fileStoreUrl || '',
		};
	};

/**
 * Creates a Media Auth provider that can be used for both view and upload.
 * The specific token is created/supplied based on the AuthContext parameters.
 * Currently, the upload token is only supplied for file and collection `update` action, used for managing Video Captions.
 * Media should integrate all the other operations before we can remove viewMediaClientConfig and uploadMediaClientConfig in favour of this one.
 * */
export const createViewAndUploadAuthProvider =
	(
		viewDataGetter: AuthProviderDataGetter,
		uploadDataGetter: AuthProviderDataGetter,
	): MediaAuthProvider =>
	async (params) => {
		// Currently, supplying upload token only for file and collection update action.
		// If no params are provided, the view token is returned.
		const isUpdateAction = !!params?.access?.find(
			({ type, actions }) =>
				(type === 'file' || type == 'collection') && actions.includes('update'),
		);

		const data = await (isUpdateAction ? uploadDataGetter() : viewDataGetter());
		return {
			token: data?.token || '',
			clientId: data?.config?.clientId || '',
			baseUrl: data?.config?.fileStoreUrl || '',
		};
	};

export const getAuthFromContext = async (contextId: string): Promise<AuthProvider> => {
	const { collectionId, templateId, spaceKey } = getTemplateMediaContextValues(contextId);

	// template media informtion is stored slightly different in the context
	// to be able to discern which call to make
	if (collectionId || templateId) {
		return getTemplateMediaAuth({ collectionId, spaceKey, templateId });
	}

	const response = await fetchMediaToken(contextId);
	const { clientId = '', fileStoreUrl = '' } = response?.data?.mediaConfiguration || {};

	return {
		clientId,
		token: response?.data?.content?.nodes?.[0]?.body?.atlas_doc_format?.mediaToken?.token || '',
		baseUrl: fileStoreUrl || '',
	};
};

const cachedGetUploadData = cacheWithExp<TokenData>(
	async (options: EditorMediaProviderOptions) => {
		const response =
			options.contentType === 'comment' &&
			options.cloudId &&
			fg('confluence_comment_use_comment_media_endpoint')
				? await getCommentUploadToken(options.contentId, options.cloudId)
				: await getUploadToken(options.contentId);
		const session = response?.data?.contentMediaSession;
		return {
			token: session?.token?.value || '',
			config: session?.configuration || DEFAULT_CONFIG,
			collectionId: session?.collection || '',
		};
	},
	(json) => json.token,
);

const getUploadCollectionId = async (options: ConfigOptions) => {
	const uploadData = await cachedGetUploadData.getValue(options);
	return uploadData.collectionId;
};

const createUploadAuthProviderDataGetter = (options: EditorMediaProviderOptions) => () =>
	cachedGetUploadData.getValue(options);

const getUploadMediaClientConfig = async (
	options: EditorMediaProviderOptions,
): Promise<MediaClientConfig> => ({
	authProvider: createAuthProvider(createUploadAuthProviderDataGetter(options)),
	getAuthFromContext,
	useSha256ForUploads: true,
});

const cachedGetExportDspData = cacheWithExp<{
	isExportAllowed: boolean | undefined | null;
	exp: number;
}>(
	async ({ contentId, spaceKey }: { contentId: string; spaceKey?: string }) => {
		const response = await getIsExportEnabledForContent(contentId, spaceKey);
		const isExportAllowed = response?.data?.dataSecurityPolicy?.isActionEnabledForContent?.allowed;
		return {
			isExportAllowed,
			exp: Date.now() + 1000 * 60, // 1 minute
		};
	},
	(json) => json.exp,
);

const getEnforceDataSecurityPolicy = async (options: ConfigOptions): Promise<boolean> => {
	if (!fg('dlp_attachment_download_respects_data_export_dsp')) return false;
	const { contentId, spaceKey, mediaDspContentId } = options;
	if (!fg('dlp_allow_content_dsp_check_without_space_details')) {
		// Note: If you can't provide spaceKey, you can't enable downloads in media viewer
		if (!spaceKey) return true;
	}
	try {
		const relevantContentId = mediaDspContentId || contentId;
		const response = await cachedGetExportDspData.getValue({
			contentId: relevantContentId,
			spaceKey,
		});
		const isExportAllowed = response?.isExportAllowed;
		return !isExportAllowed;
	} catch (e) {
		// Fails CLOSED
		return true;
	}
};

const createViewAuthProviderDataGetter = (options: ConfigOptions) => async () => {
	// Only pass contentId and isExternalShare to the cacheWithExp function
	// to avoid unnecessary re-fetching of the token if other options differ
	const authQueryOptions = {
		contentId: options.contentId,
		isExternalShare: options.isExternalShare,
	};
	let data: TokenData = await cachedGetDownloadData.getValue(authQueryOptions);

	if (options.mediaToken) {
		data = {
			...data,
			token: options.mediaToken,
		};
	}

	// For drafts mediaToken by default is null and created once any media added to them
	// So, once publishing draft that just got media but token was cached before as `null`
	// we need to re-fetch the token without the cache
	if (!data.token) {
		data = await cachedGetDownloadData.forceGetValue(authQueryOptions);
	}

	return data;
};

export const getViewMediaClientConfig = async (
	options: ConfigOptions,
): Promise<MediaClientConfig> => {
	return {
		enforceDataSecurityPolicy: await getEnforceDataSecurityPolicy(options),
		authProvider: createAuthProvider(createViewAuthProviderDataGetter(options)),
		getAuthFromContext,
	};
};

export const getViewAndUploadMediaClientConfig = async (
	options: EditorMediaProviderOptions,
): Promise<MediaClientConfig> => ({
	enforceDataSecurityPolicy: await getEnforceDataSecurityPolicy(options),
	authProvider: createViewAndUploadAuthProvider(
		createViewAuthProviderDataGetter(options),
		createUploadAuthProviderDataGetter(options),
	),
	getAuthFromContext,
});

export const createRendererMediaProvider = async (
	options: ConfigOptions,
): Promise<MediaProvider> => ({
	viewMediaClientConfig: await getViewMediaClientConfig(options),
});

export const createEditorMediaProvider = async (
	options: EditorMediaProviderOptions,
): Promise<MediaProvider> => {
	registerGlobalFileAddedListener(options);

	const [
		viewMediaClientConfig,
		uploadMediaClientConfig,
		viewAndUploadMediaClientConfig,
		collection,
	] = await Promise.all([
		getViewMediaClientConfig(options),
		getUploadMediaClientConfig(options),
		fg('platform_media_video_captions') ? getViewAndUploadMediaClientConfig(options) : undefined,
		getUploadCollectionId(options),
	]);

	return {
		viewMediaClientConfig,
		uploadParams: {
			collection,
			onUploadRejection: options.onUploadRejection,
		},
		uploadMediaClientConfig,
		viewAndUploadMediaClientConfig,
	};
};

// Control Data export while in PDF Export page
export const controlDataExportOnCertainFeatures = (pathname: string): boolean => {
	return !!PDF_EXPORT_PAGE.match(pathname);
};

let previousAttachUploadedMediaListener: (fileState: FileState) => void;

const registerGlobalFileAddedListener = (options: EditorMediaProviderOptions) => {
	if (previousAttachUploadedMediaListener) {
		globalMediaEventEmitter.off('file-added', previousAttachUploadedMediaListener);
	}
	previousAttachUploadedMediaListener = attachUploadedMedia(options);
	globalMediaEventEmitter.on('file-added', previousAttachUploadedMediaListener);
};

/**
 * Dynamic (getter-based) variants ---------------------------------------------------------------
 * These allow creating auth providers that always read the latest "options" instead of
 * closing over the snapshot present at creation time. Useful when you want to keep a single
 * MediaProvider instance stable across navigation (e.g. contentId changes) while ensuring
 * new token fetches use fresh parameters.
 *
 * The existing exported helpers (createEditorMediaProvider, createRendererMediaProvider, etc.)
 * intentionally snapshot their options. Keep those for current behaviour. Use the *Dynamic
 * variants below when you suspect stale closures.
 */

type OptionsGetter = () => ConfigOptions;
type EditorOptionsGetter = () => EditorMediaProviderOptions;

const createViewAuthProviderDataGetterDynamic = (getOptions: OptionsGetter) => async () => {
	const options = getOptions();
	return createViewAuthProviderDataGetter(options)();
};

const createUploadAuthProviderDataGetterDynamic = (getOptions: EditorOptionsGetter) => async () => {
	const options = getOptions();
	return createUploadAuthProviderDataGetter(options)();
};

export const getViewMediaClientConfigDynamic = async (
	getOptions: OptionsGetter,
): Promise<MediaClientConfig> => {
	const options = getOptions();
	return {
		enforceDataSecurityPolicy: await getEnforceDataSecurityPolicy(options),
		authProvider: createAuthProvider(createViewAuthProviderDataGetterDynamic(getOptions)),
		getAuthFromContext,
	};
};

export const getViewAndUploadMediaClientConfigDynamic = async (
	getOptions: EditorOptionsGetter,
): Promise<MediaClientConfig> => {
	const options = getOptions();
	return {
		enforceDataSecurityPolicy: await getEnforceDataSecurityPolicy(options),
		authProvider: createViewAndUploadAuthProvider(
			createViewAuthProviderDataGetterDynamic(getOptions),
			createUploadAuthProviderDataGetterDynamic(getOptions),
		),
		getAuthFromContext,
	};
};

export const createRendererMediaProviderDynamic = async (
	getOptions: OptionsGetter,
): Promise<MediaProvider> => ({
	viewMediaClientConfig: await getViewMediaClientConfigDynamic(getOptions),
});

export const createEditorMediaProviderDynamic = async (
	getOptions: EditorOptionsGetter,
): Promise<MediaProvider> => {
	// We deliberately do NOT register the global listener dynamically each token fetch.
	// Instead we register once per provider creation with the then-current options snapshot.
	// If you need the listener to react to changed options, recreate the provider instead.
	const initialOptions = getOptions();
	registerGlobalFileAddedListener(initialOptions);

	const [
		viewMediaClientConfig,
		uploadMediaClientConfig,
		viewAndUploadMediaClientConfig,
		collection,
	] = await Promise.all([
		getViewMediaClientConfigDynamic(() => getOptions()),
		(async () => {
			const opts = getOptions();
			return getUploadMediaClientConfig(opts);
		})(),
		fg('platform_media_video_captions')
			? getViewAndUploadMediaClientConfigDynamic(() => getOptions())
			: undefined,
		(async () => {
			const opts = getOptions();
			return getUploadCollectionId(opts);
		})(),
	]);

	return {
		viewMediaClientConfig,
		uploadParams: {
			collection,
			onUploadRejection: initialOptions.onUploadRejection,
		},
		uploadMediaClientConfig,
		viewAndUploadMediaClientConfig,
	};
};

/**
 * Usage sketch (React):
 * const liveOptionsRef = useRef(currentOptions);
 * liveOptionsRef.current = currentOptions; // update on changes
 * const providerPromise = useMemo(() => createEditorMediaProviderDynamic(() => liveOptionsRef.current), []);
 * editorApi.media.actions.setProvider(providerPromise);
 */
